import {
  inject,
  Injectable,
  OnDestroy,
  signal,
  WritableSignal,
} from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject, takeUntil } from 'rxjs';

import { CognitoHttpService } from '@core/http/cognito/cognito-http.service';
import { AuthService } from './auth.service';
import { sleep } from '@shared/utils/utils';

@Injectable({
  providedIn: 'root',
})
export class PasswordManagerService implements OnDestroy {
  private readonly _cognitoHttpService = inject(CognitoHttpService);
  private readonly _router = inject(Router);
  private readonly _authService = inject(AuthService);

  private readonly _subscription$ = new Subject<void>();
  readonly tokenVerification$ = new BehaviorSubject<boolean>(false);

  readonly resetPasswordTokenSignal: WritableSignal<string> =
    signal<string>('');
  readonly resetPasswordUsernameSignal: WritableSignal<string> =
    signal<string>('');
  readonly resetPasswordUserPoolIdSignal: WritableSignal<string> =
    signal<string>(
      this._authService.selectedProviderConfig()?.Auth.Cognito.userPoolId ?? '',
    );

  callTokenVerification(): Observable<void> {
    return this._cognitoHttpService.verifyToken(
      this.resetPasswordUsernameSignal(),
      this.resetPasswordTokenSignal(),
      this.resetPasswordUserPoolIdSignal(),
    );
  }

  ngOnDestroy(): void {
    this._subscription$.complete();
    this._subscription$.unsubscribe();
  }

  requestForgottenUserPasswordEmail(username: string): void {
    this._cognitoHttpService
      .requestForgottenPasswordEmail(
        username,
        this.resetPasswordUserPoolIdSignal(),
      )
      .pipe(takeUntil(this._subscription$))
      .subscribe({
        complete: () => {
          sleep(2000);
          this._router.navigateByUrl('/login');
        },
      });
  }

  requestUserPasswordReset(
    username: string,
    newPassword: string,
    token: string,
  ): void {
    this._cognitoHttpService
      .requestUserPasswordReset(
        username,
        newPassword,
        token,
        this.resetPasswordUserPoolIdSignal(),
      )
      .pipe(takeUntil(this._subscription$))
      .subscribe({
        complete: () => {
          sleep(2000);
          this._router.navigateByUrl('/login');
        },
      });
  }
}
